import { Alert, Button, Column, Divider, Heading, Icon, Loader, Row, Text, Textarea, Toggle, Tooltip } from '@amzn/meridian';
import type { TextColor } from '@amzn/meridian/text/text';
import trashTokens from '@amzn/meridian-tokens/base/icon/delete';
import sparklesLargeTokens from '@amzn/meridian-tokens/base/icon/sparkles-large';
import { DimmingWrapper } from 'components/DimmingWrapper';
import { ErrorAlert } from 'components/ErrorAlert';
import { useGlobalToasterContext } from 'components/GlobalToaster/GlobalToasterContext';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-final-form';
import { useParams } from 'react-router-dom';
import { stylesheet } from 'typestyle';
import type { ValidateFlavorContentFn } from '../../campaign-configs/types';
import { useCampaignConfig } from '../../hooks/useCampaignConfig';
import type { FlavorPath } from '../../mcc.types';
import { CopyCampaignContentSection } from './CopyCampaignContentSection';
import { PrettyPrintJSON } from './PrettyPrintJSON';
import { useAsyncParseJSONAndValidate } from './useAsyncParseAndValidate';
import { type UploadStatus, useExtractAndUploadEmbeddedAssets } from './useExtractAndUploadEmbeddedAssets';

/**
 * Styles for the component
 */
const css = stylesheet({
  imageContainer: {
    display: 'grid',
    minHeight: '100px',
    minWidth: '100px',
    placeItems: 'center',
  },
  imageUploadedPreview: {
    maxHeight: '100px',
    maxWidth: '200px',
    objectFit: 'contain',
  },
  textAreaWrapper: {
    $nest: {
      textarea: {
        fontFamily: `"Amazon Ember Monospace", Courier, monospace`,
      },
    },
  },
});

const STATUS_TO_COLOR_MAP: Record<UploadStatus, TextColor> = {
  CREATING: 'primary',
  ERROR: 'error',
  PENDING: 'secondary',
  SUCCESS: 'success',
  UPLOADING: 'primary',
};

/**
 * Props for the CopyPasteContentModalBody component
 *
 * @typedef {Object} CopyPasteContentModalProps
 * @property {FlavorPath} pathToFlavor - Path to the flavor content in the form data
 * @property {Function} onCloseModal - Function to close the modal
 */
type CopyPasteContentModalProps = {
  pathToFlavor: FlavorPath;
  onCloseModal: () => void;
};

/**
 * The body content of the Copy Paste Content Modal
 * This component allows users to paste JSON content for creative campaigns,
 * validates the content, extracts and uploads embedded assets,
 * and provides feedback on the validation status.
 *
 * @param {CopyPasteContentModalProps} props - Component props
 * @returns {React.ReactNode} The rendered component
 */
export const CopyPasteContentModalBody = ({ pathToFlavor, onCloseModal }: CopyPasteContentModalProps): React.ReactNode => {
  const config = useCampaignConfig();
  const { trafficProjectId } = useParams<{
    trafficProjectId: string;
  }>();

  const formApi = useForm();
  const { onOpenToast } = useGlobalToasterContext();

  const [rawText, setRawText] = useState('');
  const [ignoreValidationIssues, setIgnoreValidationIssues] = useState(false);
  const [processedContent, setProcessedContent] = useState<Record<string, unknown> | null>(null);
  const { extractAndUploadEmbeddedAssets, isUploading, assetDisplayDataArray } = useExtractAndUploadEmbeddedAssets();

  const [showUploadSection, setShowUploadSection] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState('');

  /**
   * Handles changes to the raw text input
   * Resets processed content and upload section state
   *
   * @param {string} newRawTextValue - The new text value
   */
  const handleChangeRawText = useCallback((newRawTextValue: string) => {
    setRawText(newRawTextValue);
    setProcessedContent(null);
    setShowUploadSection(false);
  }, []);

  /**
   * Validates the parsed JSON content using the campaign configuration's validation function
   *
   * @param {Record<string, unknown> | unknown} someContent - The parsed content to validate
   * @returns {Promise<ObjectLikeValidationResult>} The validation result
   */
  const validationFn = useCallback(
    async (someContent: Record<string, unknown> | unknown) => {
      // reset ignore validation button state whenever content changes
      setIgnoreValidationIssues(false);
      const { contentConfiguration } = config.contentSection;
      const validateFlavorContent = config.contentSection.validateFlavorContent;
      if (!validateFlavorContent) {
        throw new Error('No content validator found for this campaign type');
      }
      if (!someContent) {
        throw new Error('No content given');
      }
      return (validateFlavorContent as ValidateFlavorContentFn<unknown, unknown>)({
        allFormValues: formApi.getState().values,
        content: someContent,
        contentConfiguration,
      });
    },
    [
      config,
      formApi.getState,
    ],
  );

  const { isValid, isValidating, parsedData, parseError, validationError, validationResult } = useAsyncParseJSONAndValidate({
    jsonString: rawText,
    validationFn,
  });

  /**
   * Formats the JSON text for better readability
   * Uses 2-space indentation for consistent formatting
   */
  const handleFormatText = useCallback(() => {
    try {
      // can throw an error (incredibly rarely)
      const newText = JSON.stringify(parsedData, null, 2);
      setRawText(newText);
    } catch (error) {
      // log error for details
      console.error('Error formatting text', error);
      const errorMessage = error instanceof Error ? `Failed to format JSON: ${error.message}` : 'Failed to format JSON';
      onOpenToast({
        alertType: 'error',
        timeout: 2000,
        toastMessage: errorMessage,
      });
    }
  }, [
    parsedData,
    onOpenToast,
  ]);

  const ignoreIssuesToggleDisabled = useMemo(
    () => !parsedData || isValidating || isValid,
    [
      isValidating,
      parsedData,
      isValid,
    ],
  );

  const processContentButtonDisabled = useMemo(() => {
    // Always enable if content is valid
    if (isValid) return false;
    if (isUploading) return true;
    if (isValidating) return true;
    if (processedContent) return true;
    // Allow users to override simple validation errors
    return !ignoreValidationIssues;
  }, [
    ignoreValidationIssues,
    isValid,
    isUploading,
    isValidating,
    processedContent,
  ]);

  const submitButtonDisabled = useMemo(() => {
    // Always enable if content is valid
    if (!processedContent) return true;
    if (isValid) return false;
    if (isValidating) return true;
    if (isUploading) return true;
    // Allow users to override simple validation errors
    return !ignoreValidationIssues;
  }, [
    ignoreValidationIssues,
    isValid,
    isUploading,
    processedContent,
    isValidating,
  ]);

  /**
   * Processes the content to extract and upload embedded assets
   * Updates the state based on the extraction result
   */
  const handleProcessContent = useCallback(async () => {
    if (processContentButtonDisabled) return;
    if (!parsedData) return;

    setShowUploadSection(true);
    setUploadErrorMessage('');

    try {
      const extractionResult = await extractAndUploadEmbeddedAssets({
        projectId: trafficProjectId,
        someContent: parsedData,
      });

      if (!extractionResult.success) {
        setUploadErrorMessage(extractionResult.errorMessage);
        return;
      }

      if (extractionResult.success) {
        setProcessedContent(extractionResult.newContent);
      }
    } catch (useContentError) {
      console.error('error in handleUseContent', useContentError);
      const errorMessage = useContentError instanceof Error ? useContentError.message : 'Failed to use content';
      setUploadErrorMessage(errorMessage);
      onOpenToast({
        alertType: 'error',
        timeout: 2000,
        toastMessage: errorMessage,
      });
    }
  }, [
    parsedData,
    extractAndUploadEmbeddedAssets,
    onOpenToast,
    trafficProjectId,
    processContentButtonDisabled,
  ]);

  /**
   * Applies the processed content to the form and closes the modal
   */
  const handleUseContent = useCallback(() => {
    if (!processedContent) return;
    formApi.change(`${pathToFlavor}.content`, processedContent);
    formApi.blur('name');
    onCloseModal();
  }, [
    formApi.blur,
    formApi.change,
    pathToFlavor,
    processedContent,
    onCloseModal,
  ]);

  return (
    <Column spacingInset="300">
      <DimmingWrapper dimActive={isUploading}>
        <Column>
          <Alert size="medium" type="warning">
            Copy + Paste Creative Content is experimental!!!!! It may break things. Proceed with caution. Do not use on real creatives
            yet...
          </Alert>
          <CopyCampaignContentSection pathToFlavor={pathToFlavor} />
          <Divider />
          <Row
            spacing={'200'}
            widths={[
              'fill',
              'fit',
              'fit',
            ]}
          >
            <Text type="h100">New creative content</Text>
            <Tooltip position="start" title="Format JSON">
              <Button
                data-cy={`copycontent-format-json-button`}
                disabled={!!parseError || !rawText || isValidating || !parsedData}
                onClick={handleFormatText}
                type="icon"
              >
                <Icon tokens={sparklesLargeTokens} />
              </Button>
            </Tooltip>
            <Tooltip position="top" title="Clear">
              <Button disabled={isValidating} onClick={() => setRawText('')} size="small" type="icon">
                <Icon tokens={trashTokens}>Clear</Icon>
              </Button>
            </Tooltip>
          </Row>

          <div className={css.textAreaWrapper}>
            <Textarea onChange={handleChangeRawText} resize="none" rows={rawText.length > 1 ? 15 : 2} value={rawText} />
          </div>

          {/* Dim active alert or debug output when user is making changes */}
          <DimmingWrapper dimActive={isValidating}>
            {parseError && <ErrorAlert contextMessage="Failed to parse content JSON" error={new Error(parseError)} />}

            {validationResult && (
              <>
                <Text type="h100">Content validation result</Text>
                <PrettyPrintJSON objectToPrint={validationResult} />
              </>
            )}

            {validationError && <ErrorAlert contextMessage="Content validation failed" error={new Error(validationError)} />}

            {isValid && <Alert type="success">Content is valid</Alert>}
          </DimmingWrapper>
        </Column>
      </DimmingWrapper>
      {showUploadSection && (
        <>
          <Heading level={4}>Content Assets</Heading>
          {assetDisplayDataArray.length === 0 && <Text>No assets found in content</Text>}
          {uploadErrorMessage && <ErrorAlert contextMessage="Asset upload failed" error={new Error(uploadErrorMessage)} />}
          {assetDisplayDataArray.map(
            (
              {
                uploadTrackingId,
                uploadProgressPercentage,
                dimensions,
                fileName,
                uploadStatusText,
                uploadStatus,
                errorMessage,
                contentPaths,
                localPreviewUrl,
              },
              index,
            ) => (
              <Fragment key={uploadTrackingId}>
                <Column>
                  <Row>
                    <Text>{index + 1}</Text>
                    <div className={css.imageContainer}>
                      {uploadStatus === 'SUCCESS' && localPreviewUrl && <img className={css.imageUploadedPreview} src={localPreviewUrl} />}
                      {(uploadStatus === 'CREATING' || uploadStatus === 'UPLOADING') && (
                        <Loader percentage={uploadStatus === 'UPLOADING' ? uploadProgressPercentage : undefined} />
                      )}
                    </div>
                    <Column spacing={'none'}>
                      <span>
                        <Text tag="span" type="h100">
                          Name:&nbsp;
                        </Text>
                        <Text tag="span">{fileName}</Text>
                      </span>
                      <span>
                        <Text tag="span" type="h100">
                          Dimensions (px):&nbsp;
                        </Text>
                        <Text tag="span">
                          <kbd>{`${dimensions.width} x ${dimensions.height}`}</kbd>
                        </Text>
                      </span>
                      <span>
                        <Text tag="span" type="h100">
                          Status:&nbsp;
                        </Text>
                        <Text color={STATUS_TO_COLOR_MAP[uploadStatus]} tag="span">{`${uploadStatusText}`}</Text>
                      </span>
                      <span>
                        <Text tag="span" type="h100">
                          Times used:&nbsp;
                        </Text>
                        <Text tag="span">{contentPaths.length}</Text>
                      </span>
                      {errorMessage && <Text>{`${errorMessage}`}</Text>}
                    </Column>
                  </Row>
                </Column>
                {index !== assetDisplayDataArray.length - 1 && <Divider />}
              </Fragment>
            ),
          )}
        </>
      )}

      <Divider />
      <Row alignmentHorizontal="end" alignmentVertical="center">
        <Toggle
          checked={ignoreValidationIssues}
          disabled={ignoreIssuesToggleDisabled}
          onChange={() => setIgnoreValidationIssues((currentValue) => !currentValue)}
        >
          Ignore issues
        </Toggle>
        <Button disabled={processContentButtonDisabled} onClick={handleProcessContent} type="primary">
          Parse and upload assets
        </Button>
        <Button disabled={submitButtonDisabled} onClick={handleUseContent} type="primary">
          Use pasted creative content
        </Button>
      </Row>
    </Column>
  );
};
